import React, { useState, useEffect } from 'react';

const styles = {
  section: {
    display: 'flex',
    justifyContent: 'flex-start',
    columnGap: '5px',
    alignItems: 'center',
    padding: '2rem',
    backgroundColor: '#f5f5f5',
    borderRadius: '1.5rem',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
    width: '90%',
    margin: '0 auto',
    marginTop: '8rem',
    marginBottom: '5rem',
  },
  formContainer: {
    flex: '1',
    maxWidth: '500px',
    marginRight: '15rem',
  },
  imageContainer: {
    flex: '1',
    textAlign: 'center',
    marginLeft: '5rem',
  },
  image: {
    maxWidth: '100%',
    height: 'auto',
    transition: 'opacity 0.5s ease-in-out',
  },
  button: {
    width: '50%',
    padding: '0.75rem 2rem',
    borderRadius: '2rem',
    backgroundColor: '#6A5AE0',
    color: '#fff',
    fontWeight: 'bold',
    transition: 'background-color 0.3s, transform 0.2s',
    cursor: 'pointer',
    border: 'none',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    marginLeft: '8rem',
  },
  radioGroup: {
    display: 'flex',
    gap: '1.5rem',
    marginTop: '0.5rem',
  },
  radioItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  }
};

function BetaUserFormNoCaptcha() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    device: 'android', // Default to android
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    utm_content: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [rateLimited, setRateLimited] = useState(false);
  const [retryAfter, setRetryAfter] = useState(null);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL || 'https://prepairo.ai/api';

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [images, setImages] = useState([]);

  // Set images based on device width on mount
  useEffect(() => {
    const mobileImages = [
      `${process.env.PUBLIC_URL}/mob_1.png`,
      `${process.env.PUBLIC_URL}/mob_2.png`,
      `${process.env.PUBLIC_URL}/mob_3.png`,
      `${process.env.PUBLIC_URL}/mob_4.png`,
      `${process.env.PUBLIC_URL}/mob_5.png`,
    ];
    const desktopImages = [
      `${process.env.PUBLIC_URL}/1.png`,
      `${process.env.PUBLIC_URL}/2.png`,
      `${process.env.PUBLIC_URL}/3.png`,
      `${process.env.PUBLIC_URL}/4.png`,
      `${process.env.PUBLIC_URL}/5.png`,
    ];
    if (window.innerWidth < 768) {
      setImages(mobileImages);
    } else {
      setImages(desktopImages);
    }
  }, []);

  // Extract UTM parameters on component mount
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const utmSource = queryParams.get('utm_source');
    const utmMedium = queryParams.get('utm_medium');
    const utmCampaign = queryParams.get('utm_campaign');
    const utmContent = queryParams.get('utm_content');
    setFormData(prevData => ({
      ...prevData,
      utm_source: utmSource || '',
      utm_medium: utmMedium || '',
      utm_campaign: utmCampaign || '',
      utm_content: utmContent || '',
    }));
  }, []);

  // Image slider effect (executes only if images are loaded)
  useEffect(() => {
    if (images.length === 0) return;
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [images]);

  // Rate limit countdown
  useEffect(() => {
    if (rateLimited && retryAfter > 0) {
      const expirationTime = Date.now() + (retryAfter * 1000);
      sessionStorage.setItem('rateLimitExpires', expirationTime.toString());
      const timer = setTimeout(() => {
        setRetryAfter(prev => {
          if (prev <= 1) {
            setRateLimited(false);
            sessionStorage.removeItem('rateLimitExpires');
            return null;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [rateLimited, retryAfter]);

  // Check existing rate limit on mount
  useEffect(() => {
    const rateLimitExpires = sessionStorage.getItem('rateLimitExpires');
    if (rateLimitExpires) {
      const timeRemaining = Math.ceil((parseInt(rateLimitExpires, 10) - Date.now()) / 1000);
      if (timeRemaining > 0) {
        setRateLimited(true);
        setRetryAfter(timeRemaining);
      } else {
        sessionStorage.removeItem('rateLimitExpires');
      }
    }
  }, []);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Email format is invalid';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else {
      const digits = formData.phone.replace(/\D/g, '');
      if (digits.length !== 10) {
        newErrors.phone = 'Phone number must be exactly 10 digits';
      }
    }
    if (!formData.device) {
      newErrors.device = 'Please select your device type';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Update handleChange to restrict phone input to digits only & limit to 10 characters
  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;
    if (name === 'phone') {
      newValue = value.replace(/\D/g, '').slice(0, 10);
    }
    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const parseErrorMessage = (error) => {
    if (typeof error === 'string') {
      return error;
    }
    if (error && typeof error === 'object') {
      if (error.detail) {
        if (typeof error.detail === 'string') {
          return error.detail;
        }
        if (typeof error.detail === 'object' && error.detail.msg) {
          return error.detail.msg;
        }
      }
      if (error.validation_errors) {
        return Object.values(error.validation_errors).join(', ');
      }
    }
    return 'An unexpected error occurred. Please try again.';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rateLimited) {
      setSubmitStatus({
        type: 'error',
        message: `Please wait ${retryAfter} seconds before trying again.`,
      });
      return;
    }
    if (validateForm()) {
      setIsSubmitting(true);
      setSubmitStatus(null);
      try {
        const payload = {
          ...formData,
          recaptchaToken: "bypass",
        };
        const response = await fetch(`${API_URL}/users`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });
        if (response.status === 429) {
          const retryAfterHeader = response.headers.get('Retry-After');
          let waitTime = 60;
          if (retryAfterHeader) {
            waitTime = parseInt(retryAfterHeader, 10);
          }
          setRateLimited(true);
          setRetryAfter(waitTime);
          setSubmitStatus({
            type: 'warning',
            message: `Too many requests. Please try again in ${waitTime} seconds.`,
          });
          return;
        }
        let data;
        try {
          data = await response.json();
        } catch (error) {
          console.error('Error parsing response:', error);
          data = { detail: 'Server returned an invalid response' };
        }
        if (response.ok) {
          setSubmitStatus({
            type: 'success',
            message: 'Form submitted successfully! Thank you for your entry.',
          });
          setSubmissionSuccess(true);
          setFormData({
            name: '',
            email: '',
            phone: '',
            device: 'android',
            utm_source: formData.utm_source,
            utm_medium: formData.utm_medium,
            utm_campaign: formData.utm_campaign,
            utm_content: formData.utm_content,
          });
          setTimeout(() => {
            setSubmitStatus(null);
          }, 5000);
        } else {
          let errorMessage;
          switch (response.status) {
            case 400:
              errorMessage = parseErrorMessage(data);
              break;
            case 401:
              errorMessage = 'Authentication required. Please try again.';
              break;
            case 403:
              errorMessage = 'You do not have permission to perform this action.';
              break;
            case 404:
              errorMessage = 'The requested resource was not found.';
              break;
            case 500:
              errorMessage = 'Server error. Please try again later.';
              break;
            case 503:
              errorMessage = 'Service temporarily unavailable. Please try again later.';
              break;
            default:
              errorMessage = parseErrorMessage(data);
          }
          setSubmitStatus({
            type: 'error',
            message: errorMessage,
          });
        }
      } catch (error) {
        console.error('Network or unexpected error:', error);
        setSubmitStatus({
          type: 'error',
          message: 'Could not connect to the server. Please check your internet connection and try again later.',
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
      <section className="container mx-auto my-16 px-4" style={{ marginTop: '8rem' }}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 bg-gray-100 rounded-3xl shadow-md p-8">
          <div className="relative z-10">
            <h1 className="text-2xl poppins-bold text-center text-gray-800 mb-6">
              Registration for Beta Testing
            </h1>
            {submitStatus && (
                <div className={`p-3 mb-4 text-center rounded 
              ${submitStatus.type === 'success' ? 'bg-green-100 text-green-800 border border-green-200'
                    : submitStatus.type === 'warning' ? 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                        : 'bg-red-100 text-red-800 border border-red-200'}`}>
                  {submitStatus.message}
                </div>
            )}
            {rateLimited && (
                <div className="p-3 mb-4 text-center rounded bg-yellow-100 text-yellow-800 border border-yellow-200">
                  <p>Rate limit exceeded. Please wait {retryAfter} seconds.</p>
                  <p className="text-sm mt-1">
                    Our system limits the number of submissions to prevent abuse.
                  </p>
                </div>
            )}
            {submissionSuccess ? (
                <div className="text-center py-3 mb-4">
                  <h2 className="text-xl font-semibold text-green-600 mb-2">
                    Thank you for your submission!
                  </h2>
                  <p className="text-gray-600">We appreciate your interest in our Beta Testing.</p>
                </div>
            ) : (
                <form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className={`w-full px-4 py-2 border rounded-md text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
                    />
                    {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                  </div>
                  <div className="mb-4">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`w-full px-4 py-2 border rounded-md text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                    />
                    {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                  </div>
                  <div className="mb-5">
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                      Mobile <span className="text-red-500">*</span>
                    </label>
                    <div className="flex items-center">
                  <span
                      className="inline-flex items-center px-3 py-2 bg-gray-100 border border-gray-300 rounded-l-md text-gray-600 select-none">
                    +91
                  </span>
                      <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="Enter 10 digit mobile number"
                          maxLength={10}
                          className="flex-1 px-4 py-2 border-t border-b border-r border-gray-300 rounded-r-md text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    {/*<div className="relative mt-2">*/}
                    {/*  <div className="absolute top-[-8px] left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-l-transparent border-r-8 border-r-transparent border-t-8 border-t-[#EFEEFF]"></div>*/}
                    {/*  <div className="bg-[#EFEEFF] border rounded-lg p-2 shadow-lg">*/}
                    {/*    <p className="text-[#675BD8] opacity-75 text-sm text-center animate-pulse">*/}
                    {/*      Share your number for important updates &amp; rewards.*/}
                    {/*      <br />(Dont worry, we don't spam)*/}
                    {/*    </p>*/}
                    {/*  </div>*/}
                    {/*</div>*/}
                    {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Device
                    </label>
                    <div style={styles.radioGroup}>
                      <div style={styles.radioItem}>
                        <input
                            type="radio"
                            id="device-ios"
                            name="device"
                            value="ios"
                            checked={formData.device === 'ios'}
                            onChange={handleChange}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor="device-ios" className="text-sm text-gray-700">iOS</label>
                      </div>
                      <div style={styles.radioItem}>
                        <input
                            type="radio"
                            id="device-android"
                            name="device"
                            value="android"
                            checked={formData.device === 'android'}
                            onChange={handleChange}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor="device-android" className="text-sm text-gray-700">Android</label>
                      </div>
                    </div>
                    {errors.device && (
                        <p className="mt-1 text-sm text-red-600">{errors.device}</p>
                    )}
                  </div>
                  <button
                      type="submit"
                      disabled={isSubmitting || rateLimited || submissionSuccess}
                      className={`w-full py-2 px-4 poppins-bold text-white font-medium transition duration-200 rounded-full ${
                          isSubmitting || rateLimited || submissionSuccess
                              ? 'bg-gray-400 cursor-not-allowed'
                              : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500'
                      }`}
                  >
                    {isSubmitting
                        ? 'Submitting...'
                        : rateLimited
                            ? `Wait ${retryAfter}s`
                            : submissionSuccess
                                ? 'Success!'
                                : 'Submit'}
                  </button>
                </form>
            )}
          </div>
          <div className="flex justify-center items-center">
            {images.length > 0 && (
                <img
                    src={images[currentImageIndex]}
                    alt={`Slide ${currentImageIndex + 1}`}
                    className="max-w-full h-auto transition-opacity duration-500 ease-in-out"
                />
            )}
          </div>
        </div>
      </section>
  );
}

export default BetaUserFormNoCaptcha;